import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  Switch,
  FormControlLabel,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Email as EmailIcon,
  Save as SaveIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import axiosInstance from '../../services/axiosInstance';

const EmailSettings: React.FC = () => {
  const [settings, setSettings] = useState({
    emailService: 'gmail',
    emailUser: '',
    emailPass: '',
    emailFrom: '',
    emailFromName: 'DAM System',
    orderEmailTo: '',
    enableOrderEmails: true,
    siteUrl: '',
  });
  
  const [loading, setLoading] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Hämta inställningar vid laddning
  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoadingSettings(true);
      const response = await axiosInstance.get(`/api/email-settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching email settings:', error);
      setMessage({ type: 'error', text: 'Kunde inte ladda e-postinställningar' });
    } finally {
      setLoadingSettings(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setMessage({ type: 'info', text: 'E-postinställningar sparas...' });
      
      await axiosInstance.put(`/api/email-settings`, settings);
      
      setMessage({ 
        type: 'success', 
        text: 'E-postinställningar sparade!' 
      });
    } catch (error: any) {
      console.error('Error saving email settings:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Kunde inte spara e-postinställningar' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTestEmail = async () => {
    try {
      setLoading(true);
      setMessage({ type: 'info', text: 'Skickar test-e-post...' });
      
      const response = await axiosInstance.post(`/api/email-settings/test`);
      
      setMessage({ 
        type: 'success', 
        text: response.data.message || 'Test-e-post skickad!' 
      });
    } catch (error: any) {
      console.error('Error testing email:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Kunde inte skicka test-e-post' 
      });
    } finally {
      setLoading(false);
    }
  };

  if (loadingSettings) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Laddar e-postinställningar...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 800 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <EmailIcon sx={{ mr: 2, color: 'primary.main' }} />
        <Typography variant="h5" component="h2">
          E-postinställningar
        </Typography>
      </Box>

      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }}>
          {message.text}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            SMTP-konfiguration
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="E-posttjänst"
                value={settings.emailService}
                onChange={handleChange('emailService')}
                helperText="Vanligtvis 'gmail' för Gmail"
              />
              <TextField
                fullWidth
                label="Avsändarnamn"
                value={settings.emailFromName}
                onChange={handleChange('emailFromName')}
                helperText="Namn som visas som avsändare"
              />
            </Box>

            <TextField
              fullWidth
              label="E-postadress (användare)"
              type="email"
              value={settings.emailUser}
              onChange={handleChange('emailUser')}
              helperText="Din Gmail-adress eller SMTP-användarnamn"
            />

            <TextField
              fullWidth
              label="Lösenord"
              type="password"
              value={settings.emailPass}
              onChange={handleChange('emailPass')}
              helperText="För Gmail: använd App-lösenord (inte vanliga lösenordet)"
            />

            <TextField
              fullWidth
              label="Avsändaradress"
              type="email"
              value={settings.emailFrom}
              onChange={handleChange('emailFrom')}
              helperText="E-postadress som visas som avsändare (vanligtvis samma som användare)"
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            Beställningsnotiser
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.enableOrderEmails}
                  onChange={handleChange('enableOrderEmails')}
                />
              }
              label="Aktivera e-postnotiser för nya beställningar"
            />

            <TextField
              fullWidth
              label="Mottagare för beställningar"
              type="email"
              value={settings.orderEmailTo}
              onChange={handleChange('orderEmailTo')}
              helperText="E-postadress som ska få notiser om nya beställningar"
              disabled={!settings.enableOrderEmails}
            />

            <TextField
              fullWidth
              label="Webbplatsens URL (för logotyp i e-post)"
              value={settings.siteUrl}
              onChange={handleChange('siteUrl')}
              placeholder="https://dam.fontana.se"
              helperText="URL till systemet för att visa logotyp i e-postmeddelanden (t.ex. https://dam.fontana.se)"
              disabled={!settings.enableOrderEmails}
            />
          </Box>

          <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={handleTestEmail}
              startIcon={<SendIcon />}
              disabled={loading || !settings.emailUser || !settings.emailPass}
            >
              Testa e-post
            </Button>
            
            <Button
              variant="contained"
              onClick={handleSave}
              startIcon={<SaveIcon />}
              disabled={loading}
            >
              Spara inställningar
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Alert severity="warning" sx={{ mt: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Viktigt för Gmail-användare:
        </Typography>
        <Typography variant="body2">
          1. Aktivera 2-stegsverifiering på ditt Google-konto<br/>
          2. Generera ett App-lösenord specifikt för denna applikation<br/>
          3. Använd App-lösenordet här, inte ditt vanliga Gmail-lösenord
        </Typography>
      </Alert>
    </Box>
  );
};

export default EmailSettings; 